import React from "react";
import { Link, useLocation } from "react-router-dom";
import { lt, leOn, useNow, cdParts, cdShort, p2, frDate, leLow, gaugeOn, plural, useLimited, useHp, leStatus } from "../lib/limited";
import { imgW } from "../lib/api";
import { trackEvent } from "../lib/seo";

export const Pin = ({ size = 15 }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12z" /><circle cx="12" cy="10" r="2.6" /></svg>
);

export const PickBadge = ({ hp, sm, testid }) => (
  <span className={`le-pk${sm ? " sm" : ""}`} data-testid={testid}><Pin size={sm ? 12 : 15} />{lt(hp, "pick_label")}</span>
);

export function Countdown({ end, sm }) {
  const now = useNow();
  const c = cdParts(end, now);
  return (
    <div className={`le-cd${sm ? " sm" : ""}`} role="timer" aria-label="Temps restant avant la fin de l'édition" data-testid="le-countdown">
      <div><b>{c.d}</b><span>jours</span></div><div><b>{p2(c.h)}</b><span>heures</span></div><div><b>{p2(c.m)}</b><span>min</span></div><div><b>{p2(c.s)}</b><span>sec</span></div>
    </div>
  );
}

export function CdText({ hp, end, className, testid }) {
  const now = useNow();
  return <span className={className} data-testid={testid}>{cdShort(hp, end, now)}</span>;
}

export function Gauge({ p, hp }) {
  if (!gaugeOn(p)) return null;
  const total = p.le_stock_total, left = Math.max(0, Math.min(total, p.stock ?? 0));
  const pct = Math.round(((total - left) / total) * 100);
  const low = left <= leLow(p);
  return (
    <div className={`le-gauge${low ? " low" : ""}`} role="img" aria-label={left > 0 ? `Il reste ${left} pièces sur ${total}` : "Série épuisée"} data-testid="le-gauge">
      <div className="le-gauge-top">
        <span>{left > 0 ? lt(hp, "le_gauge_left", { left, total }) : lt(hp, "le_gauge_out")}</span>
        <em>{left <= 0 ? "" : low ? lt(hp, "le_gauge_low") : lt(hp, "le_gauge_reserved", { sold: total - left })}</em>
      </div>
      <div className="le-gauge-bar" style={{ "--w": `${pct}%` }}><div className="le-gauge-fill" /><span className="le-gauge-mv"><img className="le-gauge-ck" src="/static/home/masc-cookie-orange.png" alt="" width="36" height="29" /></span></div>
    </div>
  );
}

export function LeCardLine({ p, hp }) {
  const st = leStatus(p);
  const left = p.stock ?? 0;
  const low = gaugeOn(p) && left > 0 && left <= leLow(p);
  return (
    <>
      {st === "live" && p.le_end_ts && <CdText hp={hp} end={p.le_end_ts} className="le-ck-cd" testid={`le-card-cd-${p.id}`} />}
      <span className={`le-ck-end${low ? " low" : ""}`} data-testid={`le-card-line-${p.id}`}><i />
        {st === "soldout" ? lt(hp, "le_gauge_out") : `${p.le_end_ts ? lt(hp, "le_card_line", { date: frDate(p.le_end_ts) }) : lt(hp, "le_badge")}${gaugeOn(p) && left > 0 ? lt(hp, "le_card_left", { left }) : ""}`}
      </span>
    </>
  );
}

const Ring = ({ id, text }) => (
  <svg viewBox="0 0 200 200" aria-hidden="true"><defs><path id={id} d="M100,100 m-76,0 a76,76 0 1,1 152,0 a76,76 0 1,1 -152,0" /></defs><circle cx="100" cy="100" r="99" fill="#C6531A" /><text fontSize="15" fontWeight="800" fill="#FFF8F1"><textPath href={`#${id}`} textLength="470" lengthAdjust="spacing">{text}</textPath></text></svg>
);
export const StampRing = Ring;

const soonestEnd = (live) => live.map((p) => p.le_end_ts).filter(Boolean).sort((a, b) => a - b)[0];

export function LeTopBar() {
  const hp = useHp();
  const { live } = useLimited();
  const loc = useLocation();
  if (!live.length || !leOn(hp, "le_top_on") || hp.limited_visible === false || loc.pathname.startsWith("/admin")) return null;
  const end = soonestEnd(live);
  return (
    <Link to="/editions-limitees" className="le-top" data-testid="le-top-bar">
      <style>{LE_CSS}</style>
      <span>{lt(hp, "le_top_label")}</span>
      <b className="nm">{live.length > 1 ? lt(hp, "le_top_many", { n: live.length }) : live[0].name}</b>
      {end && <CdText hp={hp} end={end} className="cdn" />}
      <span className="go">{lt(hp, "le_top_go")} →</span>
    </Link>
  );
}

function StampCount({ end }) {
  const now = useNow();
  const c = cdParts(end, now);
  const big = c.d > 0 ? c.d : c.h;
  const lab = c.d > 0 ? (c.d > 1 ? "jours" : "jour") : (c.h > 1 ? "heures" : "heure");
  return <span className="c"><b>{big}</b><span>{lab}</span></span>;
}

export function HeroStamp() {
  const hp = useHp();
  const { live } = useLimited();
  const end = soonestEnd(live);
  if (!live.length || !end || !leOn(hp, "le_hstamp_on") || hp.limited_visible === false) return null;
  return (
    <Link to="/editions-limitees" className="le-hstamp" data-testid="le-hero-stamp">
      <style>{LE_CSS}</style>
      <Ring id="leRingHome" text={lt(hp, "le_hstamp")} />
      <StampCount end={end} />
    </Link>
  );
}

export const nameHTML = (n) => {
  const w = String(n || "").trim().split(" ");
  if (w.length < 2) return n;
  const last = w.pop();
  return <>{w.join(" ")} <span className="it">{last}</span></>;
};

export function HomeLeCard() {
  const hp = useHp();
  const { live } = useLimited();
  const f = live[0];
  if (!f || !leOn(hp, "le_home_on") || hp.limited_visible === false) return null;
  return (
    <section className="le-home-sec" data-testid="le-home-card"><style>{LE_CSS}</style><div className="le-wrap">
      <Link to="/editions-limitees" className="le-home" onClick={() => trackEvent("home_discover", "edition")} data-testid="le-home-link">
        <div className="pic"><img src={imgW(f.image, 700)} alt={f.name} loading="lazy" decoding="async" width="700" height="700" /></div>
        <div className="txt">
          <p className="le-k"><i className="live" />{lt(hp, "le_home_k", { date: frDate(f.le_end_ts) })}</p>
          <h3>{nameHTML(f.name)}</h3>
          {f.pickup_only && <p className="le-pk-line"><Pin />{(f.pickup_text || "").trim() || lt(hp, "pick_default")}</p>}
          {leStatus(f) === "live" && f.le_end_ts && <Countdown end={f.le_end_ts} sm />}
          <span className="le-btn le-btn-orange">{lt(hp, "le_home_cta")} →</span>
        </div>
      </Link>
    </div></section>
  );
}

export function CookiesStrip() {
  const hp = useHp();
  const { live } = useLimited();
  const f = live[0];
  if (!f || !leOn(hp, "le_strip_on") || hp.limited_visible === false) return null;
  return (
    <Link to="/editions-limitees" className="le-strip" data-testid="le-cookies-strip"><style>{LE_CSS}</style>
      <i /><span>{lt(hp, "le_strip")} <b>{live.length > 1 ? lt(hp, "le_top_many", { n: live.length }) : f.name}</b></span>
      {f.le_end_ts && <CdText hp={hp} end={f.le_end_ts} />}<span aria-hidden="true">→</span>
    </Link>
  );
}

export const LE_CSS = `
.le-pk{position:absolute;left:12px;bottom:12px;z-index:2;display:inline-flex;align-items:center;gap:6px;background:#FFF8F1;color:#2B1A12;border:2px solid #2B1A12;font-size:12px;font-weight:800;letter-spacing:.03em;text-transform:uppercase;padding:6px 11px 6px 9px;border-radius:999px;box-shadow:0 6px 16px rgba(43,26,18,.28);font-family:'Bricolage Grotesque',ui-sans-serif,system-ui,sans-serif}
.le-pk.sm{position:static;box-shadow:none;font-size:10.5px;padding:3px 8px 3px 6px;width:fit-content;margin-top:4px}
@media (max-width:640px){.le-pk:not(.sm){left:8px;bottom:8px;font-size:10px;padding:4px 8px 4px 6px;gap:4px}}
.le-pk-line{margin:0;display:flex;align-items:center;gap:8px;font-weight:700;font-size:15px;color:#F4A06C}
@keyframes leping{0%{transform:scale(1);opacity:.55}70%,100%{transform:scale(3.2);opacity:0}}
.le-ck-end i,.le-k .live,.le-strip i{position:relative}
.le-ck-end i::after,.le-k .live::after,.le-strip i::after{content:"";position:absolute;inset:0;border-radius:50%;background:#E8431F;animation:leping 1.8s infinite}
@keyframes lespin{to{transform:rotate(360deg)}}
.le-top{display:flex;justify-content:center;align-items:center;flex-wrap:wrap;gap:4px 14px;background:#C6531A;color:#fff;padding:10px 16px;text-decoration:none;font-weight:700;font-size:14.5px;text-align:center;font-variant-numeric:tabular-nums;font-family:'Bricolage Grotesque',ui-sans-serif,system-ui,sans-serif;position:relative;z-index:49}
.le-top:hover{background:#A8410F}.le-top b{font-weight:800}.le-top .go{text-decoration:underline;text-underline-offset:3px}
.le-top .cdn{background:rgba(43,26,18,.28);border-radius:999px;padding:3px 12px}
@media (max-width:640px){.le-top .nm{display:none}}
.le-hstamp{position:absolute;z-index:3;right:clamp(14px,4vw,64px);bottom:clamp(28px,6vw,84px);width:clamp(104px,13vw,176px);aspect-ratio:1;display:grid;place-items:center;color:#fff;text-decoration:none;filter:drop-shadow(0 12px 28px rgba(0,0,0,.45));transition:transform .4s;font-family:'Bricolage Grotesque',ui-sans-serif,system-ui,sans-serif}
.le-hstamp:hover{transform:scale(1.06) rotate(-4deg)}
.le-hstamp svg{position:absolute;inset:0;width:100%;height:100%;animation:lespin 24s linear infinite}
.le-hstamp .c{position:relative;display:flex;flex-direction:column;align-items:center;line-height:1}
.le-hstamp .c b{font-size:clamp(34px,4.6vw,60px);font-weight:800;letter-spacing:-.04em;font-variant-numeric:tabular-nums}
.le-hstamp .c span{font-size:clamp(11px,1.1vw,14px);font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-top:4px}
@media (max-width:640px){.le-hstamp{bottom:auto;top:14px;right:12px;width:96px}.le-hstamp .c b{font-size:30px}}
.le-cd{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;max-width:440px;color:#FFF8F1}
.le-cd div{background:rgba(255,248,241,.08);border:1px solid rgba(255,248,241,.18);border-radius:18px;padding:12px 4px 10px;text-align:center}
.le-cd div:last-child{background:rgba(198,83,26,.55);border-color:rgba(244,160,108,.5)}
.le-cd b{display:block;font-size:clamp(28px,4vw,46px);font-weight:800;letter-spacing:-.04em;line-height:1;font-variant-numeric:tabular-nums}
.le-cd span{display:block;margin-top:6px;font-size:11.5px;letter-spacing:.12em;text-transform:uppercase;opacity:.75}
.le-cd.sm{gap:8px;max-width:420px}.le-cd.sm div{border-radius:14px;padding:9px 2px 7px}.le-cd.sm b{font-size:26px}.le-cd.sm span{font-size:10.5px;margin-top:4px}
.le-gauge{display:flex;flex-direction:column;gap:9px;max-width:440px}
.le-gauge-top{display:flex;justify-content:space-between;gap:12px;font-size:15px;font-weight:600}
.le-gauge-top em{font-style:normal;color:#F4A06C}.le-gauge.low .le-gauge-top em{color:#FF8A6B}
.le-gauge-bar{position:relative;height:12px;border-radius:999px;background:rgba(255,248,241,.16)}
.le-gauge-fill{position:absolute;left:0;top:0;bottom:0;width:var(--w);border-radius:999px;background:linear-gradient(90deg,#C6531A,#F4A06C);transform-origin:left center;animation:legfill 1.5s cubic-bezier(.2,.75,.15,1) both}
.le-gauge-mv{position:absolute;inset:0;pointer-events:none;transform:translateX(calc(var(--w) - 100%));animation:legck 1.5s cubic-bezier(.2,.75,.15,1) both}
.le-gauge.low .le-gauge-fill{background:linear-gradient(90deg,#E8431F,#FF8A6B)}
.le-gauge-ck{position:absolute;top:50%;right:0;width:36px;height:36px;object-fit:contain;padding:4px;background:#FFF8F1;border-radius:50%;transform:translate(50%,-50%) rotate(12deg);box-shadow:0 3px 10px rgba(0,0,0,.45)}
@keyframes legfill{from{transform:scaleX(0)}}@keyframes legck{from{transform:translateX(-100%)}}
.le-ck-cd{display:block;margin-top:4px;color:#B44A16;font-weight:800;font-size:15.5px;font-variant-numeric:tabular-nums}
.le-ck-end{display:flex;align-items:center;gap:8px;margin-top:3px;font-size:13.5px;font-weight:700;color:#9E3D0E;font-variant-numeric:tabular-nums}
.le-ck-end.low{color:#B8321B}
.le-ck-end i{width:7px;height:7px;border-radius:50%;background:currentColor;flex:none}
.le-wrap{max-width:1320px;margin:0 auto;padding:0 20px}
.le-home-sec{padding:0 0 clamp(56px,8vw,100px);font-family:'Bricolage Grotesque',ui-sans-serif,system-ui,sans-serif}
.le-home{position:relative;background:#2B1A12;color:#FFF8F1;border-radius:40px;padding:clamp(22px,4vw,56px);display:grid;grid-template-columns:minmax(0,.8fr) minmax(0,1.2fr);gap:clamp(22px,4vw,64px);align-items:center;overflow:hidden;isolation:isolate;text-decoration:none}
.le-home::before{content:"";position:absolute;z-index:-1;left:-12%;top:-30%;width:60%;aspect-ratio:1;background:radial-gradient(closest-side,rgba(198,83,26,.55),rgba(198,83,26,0) 70%)}
.le-home .pic{position:relative;aspect-ratio:1;border-radius:30px;overflow:hidden;transform:rotate(-2.5deg);transition:transform .6s;box-shadow:0 30px 60px rgba(0,0,0,.45);background:#EDD4BB}
.le-home .pic img{width:100%;height:100%;object-fit:cover;display:block}
.le-home:hover .pic{transform:rotate(0)}
.le-home .txt{display:flex;flex-direction:column;gap:16px;align-items:flex-start}
.le-home h3{margin:0;font-size:clamp(36px,5.2vw,76px);line-height:.92;letter-spacing:-.05em;font-weight:800}
.le-home h3 .it,.le-it{font-family:'Instrument Serif',Georgia,serif;font-style:italic;font-weight:400;letter-spacing:-.01em;color:#F4A06C}
.le-k{display:inline-flex;align-items:center;gap:10px;margin:0;color:#F4A06C;font-size:14px;font-weight:700;letter-spacing:.1em;text-transform:uppercase}
.le-k .live{width:9px;height:9px;border-radius:50%;background:#FF5A36}
.le-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:54px;padding:0 26px;border-radius:999px;font-weight:700;font-size:17px;text-decoration:none;border:0;cursor:pointer;transition:background-color .2s,color .2s,transform .2s;font-family:inherit}
.le-btn:active{transform:scale(.98)}
.le-btn-orange{background:#C6531A;color:#fff}.le-btn-orange:hover{background:#A8410F}
.le-btn-cream{background:#FFF8F1;color:#2B1A12}.le-btn-cream:hover{background:#fff}
.le-btn-ghost{background:transparent;color:#FFF8F1;border:1.5px solid rgba(255,248,241,.5)}.le-btn-ghost:hover{background:#FFF8F1;color:#2B1A12}
.le-strip{display:flex;align-items:center;justify-content:center;gap:12px;flex-wrap:wrap;background:#B44A16;color:#FFF8F1;padding:12px 20px;text-decoration:none;font-weight:700;text-align:center;font-variant-numeric:tabular-nums;font-family:'Bricolage Grotesque',ui-sans-serif,system-ui,sans-serif}
.le-strip:hover{background:#A8410F}
.le-strip i{width:8px;height:8px;border-radius:50%;background:#fff}
@media (max-width:900px){.le-home{grid-template-columns:minmax(0,1fr);border-radius:30px}}
@media (prefers-reduced-motion:reduce){.le-hstamp svg,.le-gauge-fill,.le-gauge-mv{animation:none!important}.le-k .live::after,.le-ck-end i::after,.le-strip i::after{animation:none!important;opacity:0}}
`;
